import { InterviewSession } from "../Interface/Index";
import apiClient from "./api-client.service";

class AIInterviewService {
  private APIBaseUrl: string = "";

  constructor() {
    this.APIBaseUrl = process.env.REACT_APP_API_BASE_URL ?? "";
  }

  async startAIInterview(sessionId: string, browserFingerprint: string) {
    const paylaod = {
      sessionId,
      browserFingerprint,
    };

    try {
      const response = await apiClient.post(`/interview/start`, paylaod);
      return response.data;
    } catch (error: any) {
      console.error("Error starting AI interview:", error);
    }
  }

  async getQuestionsBySessionIdAndSessionToken(sessionId: string, sessionToken: string) {
    try {
      const response = await apiClient.get(
        `${this.APIBaseUrl}/interview/questions/candidate/${sessionId}?sessionToken=${sessionToken}`
      );
      const data = response.data;
      return data;
    } catch (error: any) {
      console.error("Error fetching questions by session id:", error);
    }
  }

  async answerSingleQuestion(
    answer: string,
    sessionId: string,
    questionId: string,
    sessionToken: string
  ) {
    try {
      const formData = new FormData();
      formData.append("answer", answer);
      const response = await apiClient.put(
        `${this.APIBaseUrl}/interview/${sessionId}/questions/${questionId}/response?sessionToken=${sessionToken}`,
        formData,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      const data = response.data;
      return data;
    } catch (error: any) {
      console.error("Error answering single question:", error);
    }
  }

  async evaluateInterview(sessionId: string, sessionToken: string) {
    try {
      const response = await apiClient.post(
        `${this.APIBaseUrl}/interview/${sessionId}/evaluate?sessionToken=${sessionToken}`
      );
      return response.data;
    } catch (error: any) {
      console.error("Error evaluating interview:", error);
      return {
        success: false,
        message: "Error evaluating interview",
        data: null,
      };
    }
  }

  async updateInterviewSession(sessionId: string, session: InterviewSession) {
    try {
      const response = await apiClient.post(
        `${this.APIBaseUrl}/interview/update/${sessionId}`,
        session,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.error("Error updating interview session:", error);
    }
  }

  async getSessionToken(sessionId: string, browserFingerprint: string) {
    try {
      const response = await apiClient.get(
        `${this.APIBaseUrl}/interview/client/token?sessionId=${sessionId}&browserFingerprint=${browserFingerprint}`
      );
      return response.data;
    } catch (error: any) {
      console.error("Error getting ip address:", error);
    }
  }

  async addActivityLogs(
    sessionId: string,
    session: InterviewSession
  ): Promise<{
    isDisqualified: boolean;
    interviewSession: InterviewSession;
    jobApplication?: any;
  } | null> {
    try {
      const response = await apiClient.post(
        `${this.APIBaseUrl}/interview/${sessionId}/activity-logs`,
        session,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.error("Error adding activity logs:", error);
      return null;
    }
  }

  async storePinginfo(sessionId: string) {
    try {
      const response = await apiClient.post(
        `${this.APIBaseUrl}/interview/ping/${sessionId}`
      );
      return response.data;
    } catch (error: any) {
      console.error("Error storing ping info:", error);
    }
  }

  async recoverSessionState(
    sessionId: string,
    isEvaluationStarted: boolean
  ): Promise<InterviewSession | null> {
    try {
      const response = await apiClient.get(
        `${this.APIBaseUrl}/interview/recover/${sessionId}?isEvaluationStarted=${isEvaluationStarted}`
      );
      return response.data;
    } catch (error: any) {
      console.error(
        "AIInterviewService -> recoverSessionState: Error recovering session state:",
        error
      );
      return null;
    }
  }

  async getNextRoundSession(
    sessionId: string
  ): Promise<InterviewSession | null> {
    try {
      const response = await apiClient.get(
        `${this.APIBaseUrl}/interview/next/${sessionId}`
      );
      return response.data;
    } catch (error: any) {
      console.error(
        "AIInterviewService -> getNextRoundSession: Error getting next round session:",
        error
      );
      return null;
    }
  }

  async retryN8NWorkflow(
    sessionId: string,
    n8nResponse: any
  ): Promise<InterviewSession | null> {
    try {
      const response = await apiClient.post(
        `${this.APIBaseUrl}/interview/workflow-failure/${sessionId}`,
        n8nResponse,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.error(
        "AIInterviewService -> retryN8NWorkflow: Error retrying n8n workflow:",
        error
      );
      return null;
    }
  }
}

const aiInterviewService = new AIInterviewService();

export default aiInterviewService;
